import React, { useState, useEffect, useRef } from 'react';
import WebSocketService from '../services/WebSocketService';
import { getWebSocketUrl } from '../utils/api';
import '../App.css';

function LeaderboardPage({ viewMode }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const leaderboardLoadedRef = useRef(false);

  useEffect(() => {
    const loadLeaderboard = async () => {
      // Skip loading if leaderboard already loaded
      if (leaderboardLoadedRef.current) {
        console.log('Leaderboard already loaded, skipping...');
        return;
      }

      console.log('Loading leaderboard...');
      setLoading(true);
      setError(null);

      // Set timeout fallback
      const timeoutId = setTimeout(() => {
        console.warn('Leaderboard loading timed out');
        setError('Failed to load leaderboard (timeout)');
        setLoading(false);
      }, 5000);

      try {
        const wsUrl = getWebSocketUrl();

        // Set up one-time Ranking listener
        const handleRanking = (message) => {
          clearTimeout(timeoutId);
          const { body } = message;
          console.log('Leaderboard response received:', message);
          console.log('  Response body:', body);
          console.log('  Response body.accepted:', body?.accepted);
          console.log('  Response body.players:', body?.players);

          // Check if request was accepted (backend may now include accepted field)
          if (body && body.accepted === false) {
            console.error('Leaderboard request rejected by server');
            setError('Failed to load leaderboard (request rejected)');
          } else if (body && body.players) {
            console.log('Setting players:', body.players);
            setPlayers(body.players);
            leaderboardLoadedRef.current = true;
          } else {
            console.error('Invalid leaderboard data - missing players array');
            setError('Invalid leaderboard data received');
          }

          WebSocketService.off('Ranking', handleRanking);
          WebSocketService.disconnect();
          setLoading(false);
        };

        WebSocketService.on('Ranking', handleRanking);
        await WebSocketService.connect(wsUrl);

        // Send GetRanking request
        WebSocketService.send({
          v: 1,
          type: 'GetRanking',
          requestId: `ranking-${Date.now()}`,
          body: {
            limit: 100
          }
        });
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Failed to load leaderboard:', error);
        setError('Failed to load leaderboard');
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, []);

  return (
    <div className="leaderboard-page">
      <h2>Leaderboard</h2>
      <div className="leaderboard-content">
        {loading ? (
          <p className="loading-text">Loading leaderboard...</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : players.length === 0 ? (
          <p className="placeholder-text">No players ranked yet</p>
        ) : (
          <div className="leaderboard-table">
            <div className="leaderboard-header">
              <div className="rank-col">Rank</div>
              <div className="name-col">Player</div>
              <div className="rating-col">Rating</div>
              <div className="wins-col">Wins</div>
              <div className="losses-col">Losses</div>
              <div className="winrate-col">Win Rate</div>
            </div>
            <div className="leaderboard-rows">
              {players.map((player) => {
                const totalGames = player.wins + player.losses;
                const winRate = totalGames > 0 ? ((player.wins / totalGames) * 100).toFixed(1) : 0;

                return (
                  <div key={player.playerId} className="leaderboard-row">
                    <div className="rank-col">#{player.rank}</div>
                    <div className="name-col">{player.nickname}</div>
                    <div className="rating-col">{player.rating}</div>
                    <div className="wins-col">{player.wins}</div>
                    <div className="losses-col">{player.losses}</div>
                    <div className="winrate-col">{winRate}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeaderboardPage;
